import { isNativeTab } from "../environment";
import { openExternalLink } from "../nativeUtils/openExternalLink";

const COLLINS_ADMIN_WIDGET_ID = "Access.Hospitality.DesignMyNight.CollinsTab";

export interface WorkspaceContext {
  workspaceUrl: string;
  instanceId: string;
}

/**
 * Checks whether a URL points to the Collins Admin (collins-ui) application
 * by comparing it against the configured APP_BASE_URL.
 */
export const isCollinsAdminUrl = (url: string, appBaseUrl: string): boolean => {
  if (!url || !appBaseUrl) return false;
  return url === appBaseUrl || url.startsWith(`${appBaseUrl}/`);
};

/**
 * Extracts the path portion of a Collins Admin URL relative to the APP_BASE_URL.
 * For example, given url "https://preprodcollins.../collins/dashboard" and
 * appBaseUrl "https://preprodcollins.../collins", returns "/dashboard".
 */
export const getCollinsAdminPath = (
  url: string,
  appBaseUrl: string
): string => {
  if (!url.startsWith(appBaseUrl)) return "";
  return url.slice(appBaseUrl.length) || "/";
};

/**
 * Opens a Collins Admin link either as an Evo Workspace tab (when running
 * inside a native tab) or in a new browser tab (fallback).
 *
 * When inside Evo, sends a `LaunchProduct` postMessage to the Workspace
 * shell which opens collins-ui as a sibling Evo tab without navigating
 * away from the current browser tab.
 *
 * When not inside Evo, falls back to `openExternalLink` which opens
 * the URL in a new browser tab (or the device browser on native).
 */
export const openCollinsAdminLink = (
  url: string,
  appBaseUrl: string,
  workspace?: WorkspaceContext
): void => {
  if (!url) return;

  if (isNativeTab() && isCollinsAdminUrl(url, appBaseUrl)) {
    const message = {
      type: "LaunchProduct",
      widgetId: COLLINS_ADMIN_WIDGET_ID,
      url,
      behaviour: 0
    };

    const targetOrigin = workspace?.workspaceUrl
      ? new URL(workspace.workspaceUrl).origin
      : "*";
    window.parent.postMessage(JSON.stringify(message), targetOrigin);
    return;
  }

  openExternalLink(url);
};
